/* eslint-disable react/require-default-props */
import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Slide from '@mui/material/Slide';
import ModalHeader from './ModalHeader';
import './modal.css';
import { cx } from '../../helpers/global';
import ModalBottomButtons from './ModalBottomButtons';

const style = {
  position: 'fixed' as 'fixed',
  top: '0px',
  right: '0px',
  width: '100%',
  bgcolor: '#fff',
  overflowY: 'scroll',
  height: '100dvh',
};

interface Props {
  modalOpen: boolean;
  setModalOpen:
    | React.Dispatch<React.SetStateAction<boolean>>
    | ((open: boolean) => void);
  name: string;
  children: React.ReactNode;
  handleClose?: () => void;
  shouldScrollTop?: boolean;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  large?: boolean;
  submitButton?: React.ComponentProps<typeof ModalHeader>['submitButton'];
  secondaryButton?: React.ComponentProps<typeof ModalHeader>['secondaryButton'];
}

export default function FullPageModal({
  modalOpen,
  setModalOpen,
  name,
  children,
  handleClose,
  shouldScrollTop,
  setShouldScrollToTop,
  large = false,
  submitButton,
  secondaryButton,
}: Props) {
  const modalContentRef = React.useRef<HTMLDivElement>(null);

  const closeModal = React.useCallback(() => {
    if (handleClose) {
      handleClose();
    } else {
      setModalOpen(false);
    }
  }, [handleClose, setModalOpen]);

  function scrollToTop() {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
    if (setShouldScrollToTop) {
      setShouldScrollToTop(false);
    }
  }

  React.useEffect(() => {
    if (shouldScrollTop) {
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldScrollTop]);

  const className = cx('full-page-modal-content', large && 'large');

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      closeAfterTransition
      className="full-page">
      <Slide in={modalOpen} direction="up" mountOnEnter unmountOnExit>
        <Box sx={style} ref={modalContentRef}>
          <ModalHeader
            name={name}
            handleClose={closeModal}
            submitButton={submitButton}
            secondaryButton={secondaryButton}
          />
          <Box className={className}>{children}</Box>
          <ModalBottomButtons
            submitButton={submitButton}
            secondaryButton={secondaryButton}
          />
        </Box>
      </Slide>
    </Modal>
  );
}
